import Head from "next/head"

import {GetStaticProps, NextPage} from "next"
import {getSortedArticlesData} from "../../src/data/content";
import {MainLayout} from "../../src/components/main-layout";
import {Container, Paper, Grid, Typography, Avatar, Button} from "@mui/material";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {useTranslation} from "next-i18next";
import {Image} from "../../src/components/image"
import NextLink from "next-translate-routes/link";
import {ArticleDate} from "../../src/components/article/article-date";

interface ArticlesIndexProps {
    allArticlesData: {
        date: string
        title: string
        slug: string
        subDir: string,
        header_image: string
        authorName: string
        readingTime: number
        header_blurDataURL: string
        author_blurDataURL: string
        intro: string
    }[]
}

const ArticlesIndex: NextPage<ArticlesIndexProps> = (props: ArticlesIndexProps) => {
    const {t, i18n} = useTranslation()

    return (
        <div>
            <Head>
                <title>{t("title-articles") + " | SeaCrush"}</title>
            </Head>

            <Container maxWidth="lg" sx={{pb: 2}}>
                <section>

                    {props.allArticlesData?.map(({
                                                    slug, date, title, subDir,
                                                    header_image, authorName, readingTime,
                                                    header_blurDataURL, author_blurDataURL, intro
                                                }, idx) => (
                        <Paper key={slug} elevation={8} sx={{mt: 4}}>
                            <Grid container>
                                <Grid item xs={12} md={5}>
                                    <Image
                                        priority={idx == 0}
                                        blurDataURL={header_blurDataURL}
                                        placeholder={"blur"}
                                        src={`/content/articles/${subDir}/img/${header_image}`} width={1199}
                                        height={800} layout={"responsive"}/>
                                </Grid>
                                <Grid item xs={12} md={7} sx={{p: 2}}>
                                    <NextLink
                                              href={{
                                                  pathname: "/articles/[articleSlug]",
                                                  query: {articleSlug: slug}
                                              }}
                                              passHref
                                              legacyBehavior
                                    >
                                        <Typography component={"a"} variant={"h4"} color={"primary"}
                                                    style={{textDecoration: "none"}}>{title}</Typography>
                                    </NextLink><br/>

                                    <Grid container spacing={2} sx={{pt: 1}} alignItems={"center"}>
                                        <Grid item>
                                            <Avatar sx={{width: 64, height: 64}}
                                            >
                                                <Image
                                                    alt={authorName}
                                                    priority={idx == 0}
                                                    blurDataURL={author_blurDataURL}
                                                    placeholder={"blur"}
                                                    src={`/content/articles/${subDir}/author.webp`} width={72}
                                                    height={72}/>
                                            </Avatar>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant={"subtitle1"}>{authorName}</Typography>
                                            <Typography variant={"subtitle2"} color={"textSecondary"}><ArticleDate
                                                dateString={date}/> • {readingTime} min read</Typography>

                                        </Grid>
                                        <Grid item>
                                            <Typography variant={"body1"} color={"textSecondary"} sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: '2',
                                                WebkitBoxOrient: 'vertical',
                                            }}>
                                                {intro}</Typography>
                                        </Grid>
                                        <Grid item textAlign={"right"} flexGrow={1}>
                                            <NextLink
                                                href={{
                                                    pathname: "/articles/[articleSlug]",
                                                    query: {articleSlug: slug}
                                                }}
                                                legacyBehavior
                                                passHref
                                            >
                                                <Button component={"a"}>{t('read-more')}</Button>
                                            </NextLink>
                                        </Grid>
                                    </Grid>

                                </Grid>
                            </Grid>
                        </Paper>
                    ))}

                </section>
            </Container>
        </div>
    )
}

ArticlesIndex.getLayout = (page) => (
    <MainLayout>
        {page}
    </MainLayout>
);

export default ArticlesIndex


export const getStaticProps: GetStaticProps = async ({locale}) => {
    if (locale === 'default') {
        return {notFound: true};
    }
    const allArticlesData = getSortedArticlesData(locale)

    return {
        props: {
            allArticlesData,
            ...(await serverSideTranslations(locale as string, ['frontend']))
        }
    };
}
